import React, { lazy, Suspense, memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider } from "antd";
import Loading from "components/shared-components/Loading";
import { lightTheme, darkTheme } from "configs/ThemeConfig";
import { resources } from "lang";
import useBodyClass from "utils/hooks/useBodyClass";
import Routes from "routes";
import TxtFileService from "services/TxtFileService";
// import fetch from "auth/FetchInterceptor";
import { saveAdminSettings } from "store/slices/authSlice";
import { setPublicLogoUrl } from "store/slices/themeSlice";
import settingsService from "services/settingsService";
import SubscriberSettingsService from "services/SubscriberSettingsService";
import { RefreshProvider } from "./RefreshContext";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));

const Layouts = () => {
  const token = useSelector((state) => state.auth.token);
  const blankLayout = useSelector((state) => state.theme.blankLayout);

  const Layout = token && !blankLayout ? AppLayout : AuthLayout;

  const locale = useSelector((state) => state.theme.locale);

  const direction = useSelector((state) => state.theme.direction);

  const currentTheme = useSelector((state) => state.theme.currentTheme);

  const currentAppLocale = resources[locale];

  useBodyClass(`dir-${direction}`);

  const themeConfig =
    currentTheme === "light" ? { ...lightTheme } : { ...darkTheme };

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const res = await settingsService.getSettings("", false);
        const adminSetting = Array.isArray(res?.data) ? res.data[0] : null;
        console.log('Fetched admin settings:', adminSetting);
        dispatch(saveAdminSettings(adminSetting || null));
      } catch (e) {
        console.error(e);
        dispatch(saveAdminSettings(null));
      }
    };
    fetchAdminSettings();
  }, [dispatch]);

  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        if (token) {
          // Fetching from subscriber/getById as requested by the user
          const res = await SubscriberSettingsService.subscriberGetById(false);
          const logo = res?.data?.[0]?.logo ?? res?.data?.logo ?? null;
          if (logo) {
            dispatch(setPublicLogoUrl(logo));
          }
        } else {
          // Fetch from admin public settings for login page
          const res = await settingsService.getSettingsPublic("", false);
          const logo = res?.data?.[0]?.logo ?? res?.data?.logo ?? null;
          if (logo) {
            dispatch(setPublicLogoUrl(logo));
          }
        }
      } catch (e) {
        console.error("Error fetching logo:", e);
      }
    };
    fetchPublicSettings();
  }, [dispatch, token]);


  return (
    <ConfigProvider
      theme={themeConfig}
      direction={direction}
      locale={currentAppLocale.antd}
    >
      <Suspense fallback={<Loading cover="content" />}>
        <RefreshProvider>
          <Layout>
            <Routes />
          </Layout>
        </RefreshProvider>
      </Suspense>
    </ConfigProvider>
  );
};

export default memo(Layouts);
